import React, { FC, ReactNode } from 'react';
import styled from 'styled-components';
import { T } from 'src/shared/helpers';
import PriceIcon from 'src/shared/material_icons/device/sell.svg';
import DeleteIcon from 'src/shared/material_icons/action/delete.svg';
import { DropdownItemIcon } from '../../../../../../widgets/dropdown';
import { ConfirmDropdownItem } from '../../confirmTwice';
import { PropertiesPanelNumberField } from '../../scene-properties-panel/propertiesPanelNumberField';
import { ComposerBlock, ComposerBlockSubtitle, ComposerBlockTitle } from '../composerBlock';
import { COMPOSER_LABEL_COLUMN } from '../composerWorkspace';
import { ComposerPriceConditions } from './composerLinksEditor';
import { ComposerAttributeNode, ComposerComparisonOperator, ComposerLogicalOperator, ComposerPricingNode } from './composerAttributesModels';

// La riga dell'importo come le righe delle card: il nome a sinistra, 11px tenue; il campo a destra.
const PriceRow = styled.div`
    display: grid;
    min-width: 0;
    grid-template-columns: ${COMPOSER_LABEL_COLUMN} minmax(0, 1fr);
    align-items: center;
    gap: 10px;

    > span:first-child {
        color: var(--editor-faint, #767a7f);
        font-size: 11px;
    }
`;

/** Quando vale un prezzo, in una riga: «Always», «If Seat is Gray», «If Seat is Gray +1». */
export const composerPriceWhen = (pricing: ComposerPricingNode, attributes: ComposerAttributeNode[]) => {
    const links = pricing.links ?? [];
    if (links.length === 0) return T._('Always', 'Admin');
    const first = links[0];
    const attribute = attributes.find((candidate) => candidate.id === first.attributeID);
    const option = attribute?.options.find((candidate) => candidate.id === first.optionID);
    const verb = first.operation === ComposerComparisonOperator.NOT_EQUAL ? T._('is not', 'Admin') : T._('is', 'Admin');
    const text = attribute ? `${T._('If', 'Admin')} ${attribute.name} ${verb} ${option?.name ?? '…'}` : T._('Condition incomplete', 'Admin');
    return links.length > 1 ? `${text} +${links.length - 1}` : text;
};

/**
 * LA CARD DI UN PREZZO (prototipo, Prices; utente 2026-10-21): in testata l'importo e quando vale, aperta l'importo e le
 * condizioni. La usano la tab Prices di un'opzione e i prezzi condivisi della selezione multipla.
 */
export const ComposerPriceCard: FC<{
    id: string;
    pricing: ComposerPricingNode;
    attributes: ComposerAttributeNode[];
    /** Gli attributi che una condizione non può nominare: quelli delle opzioni a cui il prezzo appartiene. */
    excludeAttributeIDs: string[];
    open: boolean;
    onToggleOpen: () => void;
    /** Al posto dell'importo, quando le opzioni scelte ne hanno di diversi. */
    caption?: ReactNode;
    hasError?: (linkIndex: number) => boolean;
    onChange: (patch: Partial<ComposerPricingNode>) => void;
    onRemove: () => void;
}> = ({ id, pricing, attributes, excludeAttributeIDs, open, onToggleOpen, caption, hasError, onChange, onRemove }) => {
    const amount = Number(pricing.price) || 0;
    return (
        <ComposerBlock
            id={id}
            open={open}
            onToggleOpen={onToggleOpen}
            glyph={<PriceIcon />}
            title={
                <>
                    <ComposerBlockTitle $open={open}>{caption ?? `${amount >= 0 ? '+' : ''}${amount.toFixed(2)}`}</ComposerBlockTitle>
                    <ComposerBlockSubtitle>{composerPriceWhen(pricing, attributes)}</ComposerBlockSubtitle>
                </>
            }
            menu={[
                [
                    <ConfirmDropdownItem
                        key='remove'
                        icon={
                            <DropdownItemIcon>
                                <DeleteIcon />
                            </DropdownItemIcon>
                        }
                        onConfirm={onRemove}
                    >
                        {T._('Remove', 'Admin')}
                    </ConfirmDropdownItem>
                ]
            ]}
        >
            <PriceRow>
                <span>{T._('Amount', 'Admin')}</span>
                <PropertiesPanelNumberField full value={amount} step={0.01} onChange={(value) => onChange({ price: Number(value) || 0 })} />
            </PriceRow>
            <ComposerPriceConditions
                id={`${id}-conditions`}
                attributes={attributes}
                excludeAttributeIDs={excludeAttributeIDs}
                links={pricing.links ?? []}
                logicalOperator={pricing.linkOperation ?? ComposerLogicalOperator.AND}
                hasError={hasError}
                onChange={(links, linkOperation) => onChange({ links, linkOperation })}
            />
        </ComposerBlock>
    );
};
